<template>
  <article
    :id="`question-card-${index}`"
    :key="index"
    :class="[`lines-${strings}`, { 'question-base': isBaseTab }]"
  >
    <header class="question-info">
      <div class="author body-l-semibold">
        <span>{{ item.author }}</span>
        <span v-if="item.created_at" class="date body-m-regular">
          {{ dateWithMonth(item.created_at) }}
        </span>
      </div>
      <div class="question" @click="toRoute">
        <div ref="questionText" class="question-text body-l-regular-long">
          {{ item.text }}
        </div>
      </div>
      <div
        v-if="isBaseTab && isQuestionLong"
        class="more-answers in-question"
        @click="toRoute"
      >
        <span>ещё</span>
      </div>
    </header>
    <div v-if="item.replies?.length" class="answers">
      <div ref="answerText" class="all-answers">
        <div v-for="(answer, i) in item.replies" :key="i" class="answer">
          <img
            class="author-logo"
            :src="'/static/images/icon/price-team-ava.svg'"
            alt="logo"
          />
          <span class="author body-l-semibold">{{ answer.author }}</span>
          <div class="answer-text body-l-regular-long" @click="toRoute">
            <RenderHTML :seotext="answer.text" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isBaseTab && isTablet && isAnswerLong"
      :class="['more-answers']"
      @click="toRoute"
    >
      <span>ещё</span>
    </div>
  </article>
</template>

<script setup lang="ts">
import { useGlobalStore } from '~/stores/global';
import RenderHTML from '~/components/ui/RenderHTML.vue';

const emit = defineEmits(['to-route']);
const props = defineProps({
  item: {
    type: Object as () => any,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  isBaseTab: {
    type: Boolean,
    default: false,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  isTablet: {
    type: Boolean,
    default: false,
  },
});
const answerText = ref();
const questionText = ref();
const globalStore = useGlobalStore();

const strings = computed(() => {
  const h =
    !import.meta.env.SSR && questionText
      ? questionText.value?.scrollHeight
      : null;
  if (h) {
    return h < 20 ? 1 : h < 38 ? 2 : 3;
  } else {
    return 'no-elem';
  }
});

const isQuestionLong = computed(() =>
  !import.meta.env.SSR && questionText && (props.isMobile || props.isTablet)
    ? questionText.value?.scrollHeight > 54
    : false
);

const isAnswerLong = computed(() => {
  if (
    !import.meta.env.SSR &&
    answerText.value &&
    (props.isMobile || props.isTablet)
  ) {
    const h = answerText.value?.scrollHeight;
    if (props.isMobile && props.isTablet) {
      switch (strings.value) {
        case 1:
          return h > 313;
        case 2:
          return h > 275;
        case 3:
          return h > 270;
      }
    } else {
      switch (strings.value) {
        case 1:
          return h > 294;
        case 2:
          return h > 292;
        case 3:
          return h > 272;
      }
    }
  }
  return false;
});

const dateWithMonth = (date: string) =>
  new Date(date)
    .toLocaleDateString('ru', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .replace(' г.', '');

const toRoute = () => {
  if (props.isTablet || props.isMobile) {
    // @ts-ignore
    globalStore.setScrollToQuestion('question-card-' + props.index.toString());
    emit('to-route', 'voprosy');
  }
};
</script>

<style lang="scss" scoped>
article {
  &.question {
    width: 100%;
    border-bottom: 1px solid $achromatic-hover;
    padding-bottom: 16px;
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    @include md-block {
      padding-bottom: 20px;
      margin-bottom: 20px;
    }

    &--base {
      width: 280px;
      height: 392px;
      position: relative;
      overflow: hidden;
      flex-shrink: 0;
      padding: 12px;
      margin-bottom: 0;
      margin-right: 12px;
      background: $white;
      box-shadow: 0 0 16px rgba(85, 91, 143, 0.12);
      border-radius: 8px;

      &:first-child {
        margin-left: 12px;
      }

      &:last-child {
        margin-right: 12px;
      }

      @include sm-block {
        &:first-child {
          margin-left: 24px;
        }
        &:last-child {
          margin-right: 24px;
        }
      }
      @include md-block {
        width: 320px;
        height: 392px;
      }
      @include lg-block {
        width: initial;
        box-shadow: initial;
        border-radius: initial;
        height: initial;
        padding: 0 0 20px 0;
        margin: 0 0 20px 0;
        &:first-child {
          margin-left: 0;
        }
        &:last-child {
          margin-right: 0;
        }
      }

      &.question-single {
        width: calc(100% - 24px);
        @include sm-block {
          width: calc(100% - 48px);
        }
      }

      &.question-double {
        @include md-block {
          width: calc(50% - 24px - 6px);
        }
        @include lg-block {
          width: initial;
        }
      }
    }

    @include lg-block {
      border-radius: 0;
      gap: 20px;
      box-shadow: none;
      height: auto;
      flex-direction: column;
      margin-top: 0;

      &:first-child {
        border: none;
        margin-top: 0;
        padding-top: 0;
      }
      .pointer {
        cursor: default;
      }
    }
  }

  .question-info {
    overflow: hidden;
    color: #242424;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 16px;

    .author {
      display: flex;
      gap: 8px;

      .date {
        color: #86888f;
      }
    }

    @include lg-block {
      gap: 8px;
      margin-bottom: 0;
    }
  }

  .pointer {
    cursor: pointer;
  }

  .answers {
    padding-left: 32px;
    flex-grow: 1;

    .answer {
      position: relative;
      padding-top: 4px;
      gap: 8px;
      display: flex;
      flex-direction: column;

      .author-logo {
        position: absolute;
        width: 24px;
        height: 24px;
        left: -32px;
        top: 0;
        border-radius: 10px;
        @include xl-block {
          left: -38px;
          width: 28px;
          height: 28px;
        }
      }
    }

    @include md-block {
      padding-left: 56px;
    }
    @include lg-block {
      padding-left: 62px;
    }
  }

  &.question-base {
    &.last {
      margin-right: 0;
    }

    &.single {
      width: 100%;
    }

    .question-info {
      overflow: unset;
      position: relative;
      flex-shrink: 0;

      .question {
        max-height: 54px;
        overflow: hidden;
      }
    }

    .answers {
      overflow: hidden;
      padding-left: 32px;
    }

    @include xl-block {
      &.last {
        margin-right: initial;
      }
      &.single {
        width: initial;
      }
      .question-info {
        overflow: initial;
        position: initial;
        flex-shrink: initial;

        .question {
          max-height: initial;
          overflow: initial;
        }
      }
      .answers {
        overflow: initial;
        padding-left: 62px;
      }
    }

    .more-answers {
      display: block;
      position: absolute;
      bottom: 49px;
      right: 12px;
      color: #363ef8;
      padding-left: 20px;
      cursor: pointer;

      span {
        background: white;
      }

      &:after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 20px;
        background-image: linear-gradient(to left, white, rgba(0, 0, 0, 0));
      }

      @include xl-block {
        display: none;
        user-select: none;
      }
    }

    &.lines-3 {
      .answers {
        max-height: 270px;
      }

      .more-answers {
        bottom: 17px;

        &.in-question {
          bottom: 0;
          right: 0;
        }
      }
    }

    &.lines-2 {
      .answers {
        max-height: 287px;
      }

      .more-answers {
        bottom: 16px;
      }
    }

    &.lines-1 {
      .answers {
        max-height: 294px;
      }

      .more-answers {
        bottom: 13px;
      }
    }

    @include md-block {
      &.lines-3 {
        .more-answers {
          bottom: 13px;

          &.in-question {
            bottom: 0;
            right: 0;
          }
        }
      }
      &.lines-2 {
        .more-answers {
          bottom: 29px;
        }
      }
      &.lines-1 {
        .more-answers {
          bottom: 28px;
        }
      }
    }

    @include lg-block {
      .answers {
        max-height: initial;
      }
      &.lines-3,
      &.lines-2,
      &.lines-1 {
        .more-answers {
          bottom: initial;

          &.in-question {
            bottom: initial;
            right: initial;
          }
        }
      }
    }
  }
}
</style>
